import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  InputGroup,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { FaPaperPlane, FaComments, FaPaperclip, FaTimes } from "react-icons/fa";
import { userAPI, driverAPI } from "../../api/apiSystem";
import Pusher from "pusher-js";
import "./ChatBox.css";
import { useTranslation } from "react-i18next";

// Enable Pusher logging in development
Pusher.logToConsole = process.env.NODE_ENV === "development";

export default function ChatBox({ userType = "client" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [attachments, setAttachments] = useState([]);

  const pusherRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const api = userType === ("driver" || "driver_company") ? driverAPI : userAPI;
  const userRole = userType === "driver" ? "Driver" : userType === "driver_company" ? "Driver Company" : "User";

  const getInitials = (name) => {
    if (!name) return "??";

    const names = name.split(" ");
    if (names.length === 1) {
      return name.substring(0, 2).toUpperCase();
    }

    return (
      names[0].charAt(0) + names[names.length - 1].charAt(0)
    ).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return "#6c757d";

    const colors = [
      "#007bff",
      "#28a745",
      "#dc3545",
      "#fd7e14",
      "#6f42c1",
      "#e83e8c",
      "#20c997",
      "#17a2b8",
      "#ffc107",
      "#6610f2",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const Avatar = ({ name, size = 40, className = "" }) => {
    const initials = getInitials(name);
    const color = getAvatarColor(name);

    return (
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: color,
          fontSize: size * 0.4,
          minWidth: size,
        }}
      >
        {initials}
      </div>
    );
  };

  const markAsRead = async () => {
    try {
      await api.chat.markAsRead();
      setUnreadCount(0);
      // تحديث حالة is_read للرسائل في الـ state
      setMessages((prev) => prev.map((msg) => ({ ...msg, is_read: true })));
    } catch (error) {
      console.error("❌ Error marking messages as read:", error);
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    setError("");

    try {
      console.log("🔄 Fetching conversations for:", userType);
      const result = await api.chat.conversations();

      console.log("📨 Conversations API Response:", result);

      if (result.success) {
        let messagesData = [];
        let unreadCount = 0;

        if (Array.isArray(result.data.data)) {
          messagesData = result.data.data;
        } else if (
          result.data.data &&
          Array.isArray(result.data.data.messages)
        ) {
          messagesData = result.data.data.messages;
          unreadCount = result.data.data.unread_count || 0;
        } else if (result.data.data && Array.isArray(result.data.data.data)) {
          messagesData = result.data.data.data;
        } else if (
          result.data.data &&
          Array.isArray(result.data.data.conversations)
        ) {
          result.data.data.conversations.forEach((conv) => {
            if (conv.messages && Array.isArray(conv.messages)) {
              messagesData = [...messagesData, ...conv.messages];
            }
          });
        }

        console.log("📝 Processed messages data:", messagesData);
        console.log("🔴 Unread count:", unreadCount);

        setMessages(messagesData);
        setUnreadCount(unreadCount);

        // تنظيف الـ processed IDs وإضافة الجديدة
        processedMessageIds.current.clear();
        messagesData.forEach((msg) => {
          if (msg.id) {
            processedMessageIds.current.add(msg.id);
          }
        });

        // تحديد المستخدم الحالي
        if (messagesData.length > 0) {
          const userMessage = messagesData.find(
            (msg) => msg.sender && msg.sender.role_name === userRole,
          );
          if (userMessage && userMessage.sender) {
            setCurrentUser(userMessage.sender);
            console.log("👤 Current user set:", userMessage.sender);
          } else {
            // إذا لم نجد رسائل للمستخدم، نستخدم أول رسالة كمرجع
            const firstMessage = messagesData[0];
            if (
              firstMessage.receiver &&
              firstMessage.receiver.role_name === userRole
            ) {
              setCurrentUser(firstMessage.receiver);
              console.log(
                "👤 Current user set from receiver:",
                firstMessage.receiver,
              );
            }
          }
        }

        console.log("✅ Messages loaded:", messagesData.length);
      } else {
        setError(result.message || "Failed to load conversations");
      }
    } catch (error) {
      console.error("❌ Error fetching conversations:", error);
      setError("Network error occurred");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!open) return;

    console.log("🔄 Initializing Pusher for", userType, "chat");

    if (pusherRef.current) {
      console.log("🧹 Cleaning previous Pusher connection");
      pusherRef.current.disconnect();
      pusherRef.current = null;
    }

    const pusher = new Pusher("1a6dafa4504c853b7f61", {
      cluster: "mt1",
      forceTLS: true,
      enabledTransports: ["ws", "wss"],
    });

    pusherRef.current = pusher;

    // استخدام الـ customer ID الفعلي من currentUser
    const customerId = currentUser?.id || "default";
    console.log(
      "🎯 Subscribing to Pusher channel for",
      userType,
      ":",
      customerId,
    );

    const channel = pusher.subscribe(`chat.customer.${customerId}.admins`);

    const handleNewMessage = (data) => {
      console.log("📨 Pusher message received:", data);

      // ❌ منع ظهور رسائل التأكيد
      if (
        data.message === "Message sent successfully" ||
        data.message?.includes("sent successfully") ||
        data.sender?.name?.toLowerCase() === "support" &&
        data.message?.toLowerCase().includes("success")
      ) {
        console.log("🚫 Filtered system success message");
        return;
      }

      if (data.id && processedMessageIds.current.has(data.id)) {
        console.log("⚠️ Message already processed, skipping:", data.id);
        return;
      }

      if (data.id) {
        processedMessageIds.current.add(data.id);
      }

      setMessages((prev) => {
        if (data.id && prev.some((msg) => msg.id === data.id)) {
          return prev;
        }

        if (
          data.sender &&
          data.sender.admin_type === "Admin" &&
          !data.is_read
        ) {
          setUnreadCount((prev) => prev + 1);
        }

        return [...prev, data];
      });

      scrollToBottom();
    };

    channel.bind("message.sent", handleNewMessage);

    console.log(
      "🎯 Listening for Pusher events on channel:",
      `chat.customer.${customerId}.admins`,
    );

    return () => {
      console.log("🧹 Cleaning up Pusher connection");
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
  }, [open, currentUser, userType]);

  // Listen for global events to toggle/open chat from other components (e.g., mobile navbar)
  useEffect(() => {
    const handler = (e) => {
      if (e && e.type === "toggleClientChat") {
        setOpen((prev) => !prev);
      } else if (e && e.type === "openClientChat") {
        const shouldOpen = e.detail === undefined ? true : !!e.detail;
        setOpen(shouldOpen);
      }
    };

    window.addEventListener("toggleClientChat", handler);
    window.addEventListener("openClientChat", handler);

    return () => {
      window.removeEventListener("toggleClientChat", handler);
      window.removeEventListener("openClientChat", handler);
    };
  }, []);

  const handleSend = async () => {
    if (newMessage.trim() === "" && attachments.length === 0) return;

    setSending(true);
    setError("");

    try {
      console.log("🚀 Sending message as", userType + ":", newMessage.trim());

      const formData = new FormData();
      formData.append("message", newMessage.trim());

      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      const result = await api.chat.send(formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("📤 Send message response:", result);

      if (result.success) {
        setNewMessage("");
        setAttachments([]);

        // ✅ إضافة الرسالة المرسلة محلياً بدون انتظار Pusher
        if (result.data) {
          const newMsg = {
            ...result.data,
            sender: currentUser || { name: userType === "driver" || userType === "driver_company" ? "Driver" : "User" },
            is_read: true,
          };

          setMessages((prev) => {
            if (processedMessageIds.current.has(newMsg.id)) return prev;
            processedMessageIds.current.add(newMsg.id);
            return [...prev, newMsg];
          });

          scrollToBottom();
        }
      } else {
        setError(result.message || "Failed to send message");
      }
    } catch (error) {
      console.error("❌ Error sending message:", error);
      setError("Network error occurred");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files]);
    }
    // Reset file input
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    if (open) {
      console.log("🔄 Fetching conversations for", userType);
      fetchConversations();
      // وضع علامة مقروء عند فتح الشات
      markAsRead();
    } else {
      setMessages([]);
      setNewMessage("");
      setError("");
      setAttachments([]);
      processedMessageIds.current.clear();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [messages, open]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isCurrentUser = (message) => {
    return (
      currentUser && message.sender && message.sender.id === currentUser.id
    );
  };

  const handleToggleChat = () => {
    const newOpenState = !open;
    setOpen(newOpenState);

    if (newOpenState) {
      // عند فتح الشات، وضع علامة مقروء
      markAsRead();
    }
  };

  const displayName = userType === "driver" || userType === "driver_company" ? "Driver" : "User";

  const renderAttachmentPreview = () => {
    if (attachments.length === 0) return null;

    return (
      <div className="chat-attachment-preview">
        {attachments.map((file, index) => (
          <span key={index} className="chat-attachment-item">
            <FaPaperclip size={10} />
            <span className="text-truncate" style={{ maxWidth: 120 }}>
              {file.name}
            </span>
            <button
              className="remove-btn"
              onClick={() => removeAttachment(index)}
            >
              <FaTimes size={10} />
            </button>
          </span>
        ))}
      </div>
    );
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    let str = dateString;
    const ddmmyyyy = str.match(/^(\d{2})[.-](\d{2})[.-](\d{4})\s(.*)$/);
    if (ddmmyyyy) {
      str = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}T${ddmmyyyy[4]}`;
    } else if (/^\d{4}-\d{2}-\d{2}\s/.test(str)) {
      str = str.replace(" ", "T");
    }
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  };

  const renderMessageAttachments = (msgAttachments, isSent) => {
    if (!msgAttachments || msgAttachments.length === 0) return null;

    return (
      <div>
        {msgAttachments.map((attachment, index) => {
          const isImage =
            attachment.is_image ||
            (attachment.mime_type &&
              attachment.mime_type.startsWith("image/")) ||
            (attachment.file_url &&
              /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(
                attachment.file_url,
              ));

          return (
            <div key={attachment.id || index}>
              {isImage && attachment.file_url ? (
                <a
                  href={attachment.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={attachment.file_url}
                    alt={attachment.filename || "image"}
                    className="chat-bubble-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </a>
              ) : attachment.file_url ? (
                <a
                  href={attachment.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chat-file-attachment"
                >
                  <FaPaperclip size={14} />
                  <span className="text-truncate" style={{ maxWidth: 160 }}>
                    {attachment.filename}
                  </span>
                </a>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Button className="chat-toggle-btn" onClick={handleToggleChat}>
        <FaComments size={22} color="#fff" />
        {unreadCount > 0 && (
          <span className="chat-unread-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="chat-box-container">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-header-avatar">
                {/* {getInitials(currentUser?.name || displayName)} */}
                {currentUser?.image ? (
                  <img
                    src={currentUser.image}
                    alt="Driver"
                    className="img-fluid rounded-circle"
                  />
                ) : (
                  currentUser?.name
                    ? currentUser.name.charAt(0).toUpperCase()
                    : "C"
                )}
              </div>
              <div className="chat-header-text">
                <h6>{t("Support Chat")}</h6>
                <small>{t("chat_with_admin")}</small>
              </div>
            </div>
            <div className="chat-header-actions">
              {unreadCount > 0 && (
                <Badge bg="light" text="dark" pill style={{ fontSize: 11 }}>
                  {unreadCount}
                </Badge>
              )}
              <button className="chat-close-btn" onClick={() => setOpen(false)}>
                <FaTimes size={14} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="chat-body">
            {error && (
              <Alert
                variant="danger"
                className="py-2 mx-2 mb-2"
                style={{ fontSize: 13 }}
              >
                {error}
              </Alert>
            )}

            {loading ? (
              <div className="chat-empty-state">
                <Spinner animation="border" style={{ color: "#ff6600" }} />
                <p className="mt-2">{t("loading_messages")}</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-empty-state">
                <div className="empty-icon">💬</div>
                <h6>{t("no_messages")}</h6>
                <p>{t("start_conversation")}</p>
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isSent = isCurrentUser(msg);

                return (
                  <div
                    key={msg.id || idx}
                    className={`chat-message-row ${isSent ? "sent" : "received"}`}
                  >
                    <div
                      className={`chat-bubble ${isSent ? "sent" : "received"}`}
                    >
                      {(!isSent && idx === 0) ||
                      (!isSent &&
                        idx > 0 &&
                        isCurrentUser(messages[idx - 1])) ? (
                        <div className="chat-bubble-sender">
                          {msg.sender?.name || "Support"}
                        </div>
                      ) : null}

                      {renderMessageAttachments(msg.attachments, isSent)}

                      {msg.message && (
                        <div className="chat-bubble-text">{msg.message}</div>
                      )}

                      <div className="chat-bubble-meta">
                        <span>
                          {msg.created_at
                            ? (() => {
                                const d = parseDate(msg.created_at);
                                return d
                                  ? d.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : msg.created_at;
                              })()
                            : ""}
                        </span>
                        {/* {isSent && (
                          <span
                            className={`chat-tick ${msg.is_read ? "read" : "sent-tick"}`}
                          >
                            {msg.is_read ? "✓✓" : "✓"}
                          </span>
                        )}
                        {!isSent && !msg.is_read && (
                          <span style={{ color: "#ff6600", fontWeight: 600 }}>
                            {t("new")}
                          </span>
                        )} */}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Attachments Preview */}
          {renderAttachmentPreview()}

          {/* Input */}
          <div className="chat-input-area">
            <div className="chat-input-wrapper">
              <button
                className="chat-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
              >
                <FaPaperclip size={18} />
              </button>
              <Form.Control
                className="chat-text-input"
                placeholder={t("send_msg")}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                as="textarea"
                rows={1}
                style={{ resize: "none" }}
              />
            </div>
            <button
              className="chat-send-btn"
              onClick={handleSend}
              disabled={
                sending ||
                (newMessage.trim() === "" && attachments.length === 0)
              }
            >
              {sending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaPaperPlane size={16} />
              )}
            </button>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: "none" }}
            accept="image/*,.pdf,.doc,.docx,.txt"
          />
        </div>
      )}
    </>
  );
}
